import { useLocation } from '@react-router';
import { useCallback } from 'react';
import type { UseCatalogRoutingReturn } from '../../useCatalogRouting';
import type { useCatalogRegistryTelemetry } from '../../Catalog.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { CategoryMatchingHookReturn } from '../useCategoryMatching/useCategoryMatching';
import type { CatalogStorefront } from '@graphql/generated-base';
import type { CoreCatalogCategoryFieldsFragment } from '@graphql/queries/catalog.graphql.generated';
import type { CatalogCategoryType, NavigationSource, ShopNavigateClickedProps } from '../../Catalog.telemetry.types';

const deriveSourceAndTargetCategoryFields = ({
  sourceCategory,
  sourceCategoryType,
  targetCategory,
  targetCategoryType,
}: {
  sourceCategory: Maybe<CoreCatalogCategoryFieldsFragment>;
  sourceCategoryType: CatalogCategoryType;
  targetCategory: Maybe<CoreCatalogCategoryFieldsFragment>;
  targetCategoryType: CatalogCategoryType;
}) => {
  return {
    destinationProductCatalogCategoryId: targetCategory?.id,
    destinationProductCatalogCategoryPublicUrlPath: targetCategory?.publicUrlPath,
    destinationProductCatalogCategoryTitle: targetCategory?.title,
    destinationProductCatalogCategoryType: targetCategoryType,

    productCatalogCategoryId: sourceCategory?.id,
    productCatalogCategoryTitle: sourceCategory?.title,
    productCatalogCategoryPublicUrlPath: sourceCategory?.publicUrlPath,
    productCatalogCategoryType: sourceCategoryType,
  } as const;
};

export type CatalogNavigationTrackingHookArgs = {
  getCategoryFromUrl: CategoryMatchingHookReturn['getCategoryFromUrl'];
  shopStorefront: CatalogStorefront;
} & Pick<ReturnType<typeof useCatalogRegistryTelemetry>, 'shopNavigateClicked' | 'expandMobileShopMenuClick'> &
  Pick<UseCatalogRoutingReturn, 'getPublicPathFromContextualUrl'>;

export const useTrackCatalogMenuNavigation = (args: CatalogNavigationTrackingHookArgs) => {
  const { getCategoryFromUrl, getPublicPathFromContextualUrl, shopNavigateClicked, expandMobileShopMenuClick, shopStorefront } = args;
  const location = useLocation();

  const understandCategoryToNavigate = useEventCallback(
    (category: Maybe<{ id: string; title: string; publicUrlPath: string }>, sourceCategoryOverride?: Maybe<CoreCatalogCategoryFieldsFragment>) => {
      const [sourceCategory, sourceCategoryType] = getCategoryFromUrl(
        // A `null` sourceCategoryOverride is intentional
        sourceCategoryOverride !== undefined ? sourceCategoryOverride?.publicUrlPath : getPublicPathFromContextualUrl(location.pathname),
      );
      const [targetCategory, targetCategoryType] = getCategoryFromUrl(category?.publicUrlPath);

      return {
        sourceCategory,
        sourceCategoryType,
        targetCategory,
        targetCategoryType,
      } as const;
    },
  );

  // Tracking mobile navigation

  const trackCategoryNavigateAction = useCallback(
    (args: { category: Maybe<{ id: string; title: string; publicUrlPath: string }>; navigationSource: NavigationSource; overrides?: Partial<ShopNavigateClickedProps> }) => {
      const { category, navigationSource, overrides } = args;

      const processedCategory = understandCategoryToNavigate(category);

      shopNavigateClicked({
        navigationSource,
        shopStorefront,
        page: processedCategory.sourceCategoryType === 'curation' ? 'curationDetail' : processedCategory.sourceCategoryType,
        ...deriveSourceAndTargetCategoryFields(processedCategory),
        ...overrides,
      });
    },
    [shopNavigateClicked, understandCategoryToNavigate, shopStorefront],
  );

  const trackExpandMobileShopNavAction = useCallback(
    (category: Maybe<CoreCatalogCategoryFieldsFragment>, sourceCategoryOverride?: Maybe<CoreCatalogCategoryFieldsFragment>) => {
      const processedCategory = understandCategoryToNavigate(category, sourceCategoryOverride);

      expandMobileShopMenuClick({
        ...deriveSourceAndTargetCategoryFields(processedCategory),
        shopStorefront,
      });
    },
    [expandMobileShopMenuClick, shopStorefront, understandCategoryToNavigate],
  );

  return {
    trackCategoryNavigateAction,
    trackExpandMobileShopNavAction,
  } as const;
};
